import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAppSelector } from '../store/hooks';
import { selectNairaPerYuan, toDisplayNaira, formatNaira } from '../store/slices/currencySlice';
import './MyOrders.css';

const PER_PAGE = 5;

const statusLabels = {
  pending: 'Pending',
  processing: 'Processing',
  purchased: 'Purchased',
  shipped_out: 'Shipped Out',
  in_nigeria: 'In Nigeria',
  completed: 'Completed',
  refund: 'Refund',
  cancelled: 'Cancelled',
};

const statusOptions = [
  { value: '', label: 'All statuses' },
  ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
];

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: PER_PAGE, total: 0 });
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const nairaPerYuan = useAppSelector(selectNairaPerYuan);

  const fetchOrders = useCallback(async (page = 1, overrides = {}) => {
    const s = overrides.status !== undefined ? overrides.status : status;
    const from = overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
    const to = overrides.dateTo !== undefined ? overrides.dateTo : dateTo;
    try {
      setLoading(true);
      setError('');
      const params = { per_page: PER_PAGE, page };
      if (s) params.status = s;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const response = await api.get('/orders', { params });
      const data = response.data;
      setOrders(Array.isArray(data) ? data : (data.data || []));
      setPagination({
        current_page: data.current_page ?? page,
        last_page: data.last_page || 1,
        per_page: data.per_page || PER_PAGE,
        total: data.total || 0,
      });
    } catch (err) {
      console.error('Failed to fetch orders', err);
      setError(err.response?.data?.message || 'Failed to load orders.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [status, dateFrom, dateTo]);

  // Initial load: show most recent 5 orders
  useEffect(() => {
    fetchOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = (e) => {
    e.preventDefault();
    fetchOrders(1);
  };

  const clearFilters = () => {
    setStatus('');
    setDateFrom('');
    setDateTo('');
    fetchOrders(1, { status: '', dateFrom: '', dateTo: '' });
  };

  const loadPage = (page) => {
    if (page < 1 || page > pagination.last_page) return;
    fetchOrders(page);
  };

  return (
    <Layout>
      <div className="my-orders-page">
        <div className="my-orders-container">
          <h1>My Orders</h1>

          <form className="my-orders-filters" onSubmit={handleFilter}>
            <div className="my-orders-filter-group">
              <label htmlFor="my-orders-status">Status</label>
              <select
                id="my-orders-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="my-orders-select"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="my-orders-filter-group">
              <label htmlFor="my-orders-date-from">From date</label>
              <input
                id="my-orders-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="my-orders-input"
              />
            </div>
            <div className="my-orders-filter-group">
              <label htmlFor="my-orders-date-to">To date</label>
              <input
                id="my-orders-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="my-orders-input"
              />
            </div>
            <button type="submit" className="my-orders-filter-btn" disabled={loading}>
              {loading ? 'Loading...' : 'Apply filters'}
            </button>
            {(status || dateFrom || dateTo) && (
              <button type="button" className="my-orders-clear-btn" onClick={clearFilters} disabled={loading}>
                Clear filters
              </button>
            )}
          </form>

          {loading && orders.length === 0 ? (
            <p className="my-orders-loading">Loading orders...</p>
          ) : error ? (
            <p className="my-orders-error">{error}</p>
          ) : orders.length === 0 ? (
            <div className="my-orders-empty">
              <p>{(status || dateFrom || dateTo) ? 'No orders match your filters.' : 'You have no orders yet.'}</p>
              {(status || dateFrom || dateTo) ? (
                <button type="button" className="my-orders-cta" onClick={clearFilters}>Clear filters</button>
              ) : (
                <Link to="/products" className="my-orders-cta">Browse products</Link>
              )}
            </div>
          ) : (
            <>
              <p className="my-orders-summary">
                Showing {(pagination.current_page - 1) * pagination.per_page + 1}–
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} order{pagination.total !== 1 ? 's' : ''}
              </p>
              <ul className="my-orders-list">
                {orders.map((order) => (
                  <li key={order.id} className="my-orders-item">
                    <div className="my-orders-item-header">
                      <span className="my-orders-number">{order.order_number}</span>
                      <span className={`my-orders-status status-${order.status}`}>
                        {statusLabels[order.status] || order.status}
                      </span>
                    </div>
                    <div className="my-orders-item-meta">
                      <span>Placed {new Date(order.created_at).toLocaleDateString()}</span>
                      <span>{order.items?.length || 0} item(s)</span>
                    </div>
                    <div className="my-orders-item-footer">
                      <span className="my-orders-total">
                        Total: {formatNaira(toDisplayNaira(order.total, nairaPerYuan))}
                      </span>
                      <Link to={`/my-orders/${order.id}`} className="my-orders-view">View order</Link>
                    </div>
                  </li>
                ))}
              </ul>
              {pagination.last_page > 1 && (
                <div className="my-orders-pagination">
                  <button
                    type="button"
                    disabled={pagination.current_page <= 1}
                    onClick={() => loadPage(pagination.current_page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {pagination.current_page} of {pagination.last_page}
                  </span>
                  <button
                    type="button"
                    disabled={pagination.current_page >= pagination.last_page}
                    onClick={() => loadPage(pagination.current_page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MyOrders;
